/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRoomIdentifier } from "../UrlParams";
import { backendFetch, BackendError } from "../utils/backend";
import { ErrorPage, LoadingPage } from "../FullScreenView";
import { RoomAccessNotice } from "./RoomAccessNotice";
import { widget } from "../widget";

export function RoomAvailabilityGate({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { roomId } = useRoomIdentifier();
  const { t } = useTranslation();
  const [result, setResult] = useState<{
    id: string;
    error?: Error;
    unavailable?: boolean;
  }>();
  useEffect(() => {
    if (!roomId || widget) return;
    const controller = new AbortController();
    void backendFetch(
      undefined,
      `/api/rooms/${encodeURIComponent(roomId)}/availability`,
      { signal: controller.signal },
    )
      .then(() => {
        if (!controller.signal.aborted) setResult({ id: roomId });
      })
      .catch((error) => {
        if (!controller.signal.aborted)
          setResult({
            id: roomId,
            unavailable: error instanceof BackendError && error.status === 404,
            error:
              error instanceof BackendError && error.status === 404
                ? new Error(t("instance.ui.room_not_found"))
                : error,
          });
      });
    return () => controller.abort();
  }, [roomId, t]);
  if (!roomId || widget) return children;
  if (result?.id !== roomId) return <LoadingPage />;
  if (result.unavailable)
    return (
      <RoomAccessNotice
        title={t("instance.ui.room_not_found")}
        description={t("instance.ui.room_link_expired_hint")}
      />
    );
  return result.error ? (
    <ErrorPage error={result.error} widget={widget} />
  ) : (
    children
  );
}
